import React, { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ShieldCheck } from "lucide-react";

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

const ACTION_LABELS: Record<string, string> = {
  "content.approve": "Content approved",
  "content.reject": "Content rejected",
  "user.suspend": "User suspended",
  "user.unsuspend": "User unsuspended",
  "user.role_change": "Role changed",
  "user.delete": "User deleted",
  "order.cancel": "Order cancelled",
  "order.refund": "Order refunded",
};

const ACTION_COLOURS: Record<string, string> = {
  "content.approve": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "content.reject":  "bg-red-50 text-red-700 border-red-200",
  "user.suspend":    "bg-amber-50 text-amber-700 border-amber-200",
  "user.unsuspend":  "bg-blue-50 text-blue-700 border-blue-200",
  "user.role_change":"bg-purple-50 text-purple-700 border-purple-200",
  "user.delete":     "bg-red-100 text-red-800 border-red-300",
  "order.cancel":    "bg-orange-50 text-orange-700 border-orange-200",
  "order.refund":    "bg-indigo-50 text-indigo-700 border-indigo-200",
};

type AuditEntry = {
  id: number;
  adminUserId: number;
  adminName: string | null;
  adminEmail: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
};

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function AdminAuditLog() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const PAGE = 100;

  if (!user || user.role !== "admin") return <Redirect to="/" />;

  const load = useCallback(async (off: number) => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/audit-log?limit=${PAGE}&offset=${off}`, { headers: authHeaders() });
      if (!r.ok) throw new Error("Failed");
      const d = await r.json() as { items: AuditEntry[] };
      setItems(d.items);
    } catch {
      toast({
        title: "Failed to load audit log",
        variant: "destructive",
        action: <ToastAction altText="Reload page" onClick={() => window.location.reload()}>Reload</ToastAction>,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(offset); }, [load, offset]);

  return (
    <AdminLayout current="/admin/audit-log">
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck size={22} className="text-slate-600" />
          <h1 className="text-xl font-bold text-slate-800">Admin Audit Log</h1>
        </div>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-14 bg-slate-100 rounded animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-slate-400">No audit log entries yet.</div>
        ) : (
          <div className="rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Time</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Admin</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Action</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Target</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {items.map((entry, idx) => (
                  <tr key={entry.id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                      {fmtDate(entry.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800 text-xs">{entry.adminName ?? "Admin"}</div>
                      <div className="text-slate-400 text-xs">{entry.adminEmail ?? `#${entry.adminUserId}`}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${ACTION_COLOURS[entry.action] ?? "bg-slate-100 text-slate-600 border-slate-200"}`}>
                        {ACTION_LABELS[entry.action] ?? entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {entry.targetType && <span className="capitalize">{entry.targetType}</span>}
                      {entry.targetId && <span className="text-slate-400"> #{entry.targetId}</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">
                      {entry.details ? JSON.stringify(entry.details) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && (
          <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
            <span>Showing {offset + 1}–{offset + items.length}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE))}
                disabled={offset === 0}
                className="px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
              >
                ← Prev
              </button>
              <button
                onClick={() => setOffset(offset + PAGE)}
                disabled={items.length < PAGE}
                className="px-3 py-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
